import {useState, useEffect} from "react";
import {Button, Pagination, Table, Modal, message, Tag} from 'antd';

import PageHeading from "../../shared/containers/PageHeading";
import LiveSearch from "../../modules/LiveSearch";
import PageStatistics from "../../modules/PageStatistics";
import PageFilters from "../../modules/PageFilters";
import ClientCard from "../../modules/CLientCard";

import useAll from "../../shared/hooks/useAll";

import { deleteById, updateById, getAllData } from "../../shared/services/registrations";

import {columns, renderDetails, statusIcon, statusText} from "./table";
import stats, {getRegisteratedUsers, getActiveMissions, getClients, getDelegates, getPendingUsers, searchFunction, getResultData, createAddressOptions} from "./stats";
import TotalCount from "../../shared/components/TotalCount/TotalCount";
import styles from "./registrations-page.module.scss";


const RegistrationsPage = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({});
    const [updatingId, setUpdatingId] = useState(null);
    const [mobilePage, setMobilePage] = useState(1);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);

    const updateStatus = async (id, newStatus) => {
        const approve = newStatus === "verified" ? 1 : 2;
        setUpdatingId(id);
        try {
            await updateById(id, {approve});
            const applyStatus = (current) => ({
                ...current,
                hits: (current.hits || []).map((item) => item.id === id
                    ? {...item, approve, status: newStatus}
                    : item),
            });
            setData(applyStatus);
            setCurrentData(applyStatus);
            setModalContent((current) => current.id === id
                ? {...current, approve, status: newStatus}
                : current);
            message.success(approve === 1 ? "User validated" : "User rejected");
        } catch (error) {
            message.error(error?.response?.data?.message || "Could not update this user");
        } finally {
            setUpdatingId(null);
        }
    }

    const onDeleteClick = (id,action) => {
        setDeleteTarget(id,action);
    };

    const {
        search,
        statsVisible,
        filteredInfo,
        sortedInfo,
        data,
        setData,
        currentData,
        setCurrentData,
        loading,
        pagination,
        handleChange,
        resetFilters,
        resetSorters,
        handleSearch,
        toogleStatsVisible,
    } = useAll({fetchData: getAllData, searchFunction});

    const handleDelete = async (id) => {
        try {
            await deleteById(id);
            message.success("User deleted successfully");

            // update UI (remove record)
            data.hits = data.hits.filter((item) => item.id !== id);

        } catch (err) {
            console.error(err);
            message.error("Failed to delete user");
        } finally {
            setDeleteTarget(null);
        }
    };
    useEffect(() => {
        if (!deleteTarget) return;

        Modal.confirm({
            title: "Delete User",
            content: "Are you sure you want to delete this user?",
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: () => handleDelete(deleteTarget),
            onCancel: () => setDeleteTarget(null),
        });
    }, [deleteTarget]);

    const showDetails = (id) => {
        setModalOpen(true);
        const element = data.hits.find(item => item.id === id);
        setModalContent(element);
    }

    const columnsConfig = [...columns];

    columnsConfig[0].filteredValue = filteredInfo.userType || null;
    columnsConfig[2].filteredValue = filteredInfo.avgRating || null;
    columnsConfig[7].filteredValue = filteredInfo.status || null;

    columnsConfig[1].sortOrder = sortedInfo.columnKey === 'name' ? sortedInfo.order : null;
    columnsConfig[2].sortOrder = sortedInfo.columnKey === 'avgRating' ? sortedInfo.order : null;
    columnsConfig[3].sortOrder = sortedInfo.columnKey === 'email' ? sortedInfo.order : null;
    columnsConfig[4].sortOrder = sortedInfo.columnKey === 'phone' ? sortedInfo.order : null;
    columnsConfig[5].sortOrder = sortedInfo.columnKey === 'date' ? sortedInfo.order : null;

    columnsConfig[7].render = (status) => <div className="table-status">{statusIcon[status]} <span>{statusText[status]}</span></div>;
    columnsConfig[8].render = renderDetails(showDetails, updateStatus, onDeleteClick, updatingId);

    const searchData = getResultData(data, search, countries, cities);
    const searchCurrentData = getResultData(currentData, search, countries, cities);

    const hits = searchData.hits || [];
    const pageSize = 8;
    const mobileHits = hits.slice((mobilePage - 1) * pageSize, mobilePage * pageSize);

    useEffect(() => {
        setMobilePage(1);
    }, [search, countries, cities]);

    const statsValue = {
        registratedUsersCount: getRegisteratedUsers(searchCurrentData.hits),
        activeMissionsCount: getActiveMissions(searchCurrentData.hits, searchCurrentData.missions),
        clientsCount: getClients(searchCurrentData.hits),
        delegatesCount: getDelegates(searchCurrentData.hits),
        pendingCount: getPendingUsers(searchCurrentData.hits),
    }

    return (<>
        <PageHeading title="Registrations" subtitle="Overall User Data">
            <LiveSearch toogleStatsVisible={toogleStatsVisible} onChange={handleSearch} statsVisible={statsVisible}/>
        </PageHeading>
        {statsVisible && <PageStatistics items={stats} values={statsValue}/>}
        <PageFilters selectCountry={setCountries} selectCity={setCities} countries={createAddressOptions(searchCurrentData.countries)} cities={createAddressOptions(searchCurrentData.cities)} />
        <div className={styles.desktopTable}>
            <Table columns={columnsConfig} dataSource={hits} loading={loading} pagination={pagination}
                   onChange={handleChange} scroll={{x: "max-content", y: 500}} sticky={{ offsetHeader: 0 }} />
        </div>
        <div className={styles.mobileList} aria-label="Registration review list">
            {mobileHits.map((user) => (
                <article className={styles.userCard} key={user.id}>
                    <div className={styles.cardHeading}>
                        <div>
                            <h3>{user.name}</h3>
                            <p>{user.userType}</p>
                        </div>
                        <Tag color={user.status === "verified" ? "green" : user.status === "rejected" ? "red" : "gold"}>
                            {statusText[user.status]}
                        </Tag>
                    </div>
                    <dl className={styles.cardDetails}>
                        <div><dt>Email</dt><dd>{user.email}</dd></div>
                        <div><dt>Phone</dt><dd>{user.phone}</dd></div>
                        <div><dt>Registered</dt><dd>{user.date}</dd></div>
                    </dl>
                    <div className={styles.cardActions}>
                        <Button onClick={() => showDetails(user.id)}>View details</Button>
                        <Button type="primary" loading={updatingId === user.id}
                                disabled={user.status === "verified"}
                                onClick={() => updateStatus(user.id, "verified")}>Validate</Button>
                        <Button danger loading={updatingId === user.id}
                                disabled={user.status === "rejected"}
                                onClick={() => updateStatus(user.id, "rejected")}>Reject</Button>
                    </div>
                </article>
            ))}
            {!loading && mobileHits.length === 0 && <p className={styles.empty}>No registrations found.</p>}
            {hits.length > pageSize && <Pagination simple current={mobilePage} pageSize={pageSize}
                total={hits.length} onChange={setMobilePage} />}
        </div>
        <TotalCount total={searchCurrentData?.hits?.length} />
        <Modal open={modalOpen} onCancel={() => setModalOpen(false)} footer={null}
               width={720} wrapClassName={styles.registrationModal}>
            <ClientCard {...modalContent} updateStatus={updateStatus} updating={updatingId === modalContent.id}/>
        </Modal>
    </>)
}

export default RegistrationsPage;
